import dynamic from 'next/dynamic';
import { lazily } from 'react-lazily';
import { useRouter } from 'next/router';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { useState, Suspense, useMemo, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import {
    getLocaleMonthOptions,
    getYearsOptions,
    getDayOptionsForMonthYear,
    apiErrorHandler,
} from '@login/helpers';

import { calculateAgeByDate } from 'helpers/date';

import {
    postRegister,
    PostErrors,
    PostRegisterRequest,
    PostRegisterErrorResponse,
} from 'services/idm';

import { FormNavigation } from '@login/components/form-navigation';
import { ButtonsToggler } from '@login/components/buttons-toggler';
import { ApiErrorBlock } from '@login/components/api-error-block';

import styles from './register.module.scss';
import { FormBox } from '@login/components/form-box';
import { useScrollTo } from 'hooks/use-scroll-to';
import { useAppTranslation } from 'hooks/use-translates';
import { useCaseInsensitiveParam } from 'hooks/use-case-insensitive-params';
import { EMAIL_PATTERN, PASSWORD_PATTERN } from '@login/lib/constants';

const Button = dynamic(() => import('core/generic/elements/button'), {
    ssr: false,
});
const Loading = dynamic(() => import('core/generic/elements/loading'), {
    ssr: false,
});
const InfoBlock = dynamic(() => import('core/generic/elements/info-block'), {
    ssr: false,
});
const { Insert, Select, DropdownList, Phone, Hint } = lazily(
    () => import('core/generic/elements/form')
);

type RegisterFormUrls = {
    registerUrl?: string;
    signInUrl?: string;
    forgottenPasswordUrl?: string;
    privacyPolicyUrl?: string;
    supportName?: string;
};

type RegisterFormProps = {
    urls: RegisterFormUrls | null;
    minimumAge: number;
    marketingMessage: string;
    disclaimerMessage: string;
    token?: string | null;
};

export const RegisterForm = ({
    urls,
    token,
    minimumAge,
    marketingMessage,
    disclaimerMessage,
}: RegisterFormProps) => {
    const { i18n } = useTranslation();
    const { push } = useRouter();
    const { scrollToSection } = useScrollTo();
    const [apiErrorMessage, setApiErrorMessage] = useState<
        PostErrors | string | null
    >(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const { t } = useAppTranslation(urls);
    const returnUrl = useCaseInsensitiveParam('returnUrl');
    const {
        control,
        handleSubmit,
        register,
        getValues,
        setValue,
        setError,
        clearErrors,
		trigger,
        formState: { errors },
    } = useForm<PostRegisterRequest>({ mode: 'onBlur' });

    const selectedMonth = useWatch({ control, name: 'monthOfBirth' });
    const selectedYear = useWatch({ control, name: 'yearOfBirth' });
    const selectedDay = useWatch({ control, name: 'dayOfBirth' });

    const dayOptions = useMemo(
        () => getDayOptionsForMonthYear(selectedMonth, selectedYear),
        [selectedMonth, selectedYear]
    );

    useEffect(() => {
        const maxDay = dayOptions.length;
        if (+selectedDay > maxDay) {
            setValue('dayOfBirth', '1');
        }
        if (selectedDay && selectedMonth && selectedYear) {
            underMinAgeHandler(
                selectedDay,
                selectedMonth,
                selectedYear,
                minimumAge
            );
        }
    }, [selectedMonth, selectedYear, selectedDay, dayOptions.length, setValue]);

    const underMinAgeHandler = (
        day: string,
        month: string,
        year: string,
        minAge: number
    ) => {
        const birthDate = new Date(
            Number(year),
            Number(month) - 1,
            Number(day)
        );
        if (isNaN(birthDate.getTime())) {
            setError('birthDate', { message: 'Invalid birth date' });
            return;
        }

        const age = calculateAgeByDate(birthDate);

        if (age < minAge) {
            setError('birthDate', {
                message: t('register.too_young_error').replace(
                    '{minimumAge}',
                    minAge.toString()
                ),
            });
            return;
        } else clearErrors('birthDate');
    };

    const handleOnSubmit = async (formData: {
        dayOfBirth: string;
        monthOfBirth: string;
        yearOfBirth: string;
        email: string;
        password: string;
        passwordConfirm: string;
        marketingOptIn: boolean;
    }) => {
        try {
            setApiErrorMessage(null);
            setIsSubmitting(true);
            const response = await postRegister(
                {
                    dayOfBirth: formData.dayOfBirth,
                    monthOfBirth: formData.monthOfBirth,
                    yearOfBirth: formData.yearOfBirth,
                    email: formData.email,
                    password: formData.password,
                    passwordConfirm: formData.passwordConfirm,
                    marketingOptIn: formData.marketingOptIn,
                    returnUrl: returnUrl,
                },
                i18n.language,
                token
            );

            if (response.action === 'Redirect' && response.redirectUrl) {
                if (response.redirectUrl.startsWith('/connect')) {
                    const absoluteUrl = `${window.location.origin}${response.redirectUrl}`;
                    window.location.href = absoluteUrl;
                } else {
                    push(response.redirectUrl);
                }
            } else {
                setIsSubmitting(false);
            }
        } catch (e) {
            const error = e as PostRegisterErrorResponse;
            apiErrorHandler(error, i18n.language);
            if (error.title === 'VALIDATION_ERROR' && error.status === 400) {
                setApiErrorMessage(error.detail);
            } else if (
                error.title === 'VALIDATION_ERROR' &&
                error.status === 422
            ) {
                setApiErrorMessage(error.errors);
            } else if (error.title === 'USED_EMAIL_ERROR') {
                setApiErrorMessage({
                    usedEmail: t('register.email_used_error'),
                });
            } else {
                setApiErrorMessage({
                    unknownServerError: t('common.500_error_message'),
                });
            }

            setIsSubmitting(false);
        }
    };

    const renderErrorBlock = useCallback(() => {
        let message = '';

        if (typeof apiErrorMessage === "string") {
            message = apiErrorMessage;
        } else if (apiErrorMessage?.usedEmail) {
            message = apiErrorMessage.usedEmail
                .replace("{signInUrl}", urls?.signInUrl || "")
                .replace("{forgottenPasswordUrl}", urls?.forgottenPasswordUrl || "");
        } else if (errors?.birthDate?.message) {
            message = errors.birthDate.message;
        } else if (apiErrorMessage?.unknownServerError) {
            message = apiErrorMessage.unknownServerError;
        }

        return message ? <ApiErrorBlock apiErrorMessage={message} /> : null;
    }, [errors, apiErrorMessage, urls]);

    useEffect(() => {
        const arrayOfKeys: string[] = [
            'birthDate',
            'usedEmail',
            'unknownServerError',
            'dayOfBirth',
            'monthOfBirth',
            'yearOfBirth',
            'email',
            'password',
            'passwordConfirm',
            'marketingOptIn',
        ];
        let firstErrorElement = null;
        for (const key of arrayOfKeys) {
            if (apiErrorMessage?.hasOwnProperty(key)) {
                firstErrorElement = document.querySelector('[role="alert"]');
                break;
            }
            if (errors?.hasOwnProperty(key)) {
                firstErrorElement = document.getElementById(`root-${key}`);
                break;
            }
            continue;
        }
        if (firstErrorElement && 'scrollIntoView' in firstErrorElement) {
            scrollToSection({ sectionRef: firstErrorElement, offset: 120 });
        }
    }, [errors, apiErrorMessage, scrollToSection]);

    return (
        <Suspense fallback={<Loading />}>
            <form
                onSubmit={handleSubmit(handleOnSubmit)}
                id="form-profile"
                noValidate
            >
                <FormBox>
                    <FormNavigation
                        activeBtn="register"
                        signInUrl={urls?.signInUrl}
                        registerUrl={urls?.registerUrl}
                        returnUrl={returnUrl}
                    />
                    <div className={styles['form-box-content']}>
                        {renderErrorBlock()}
                        <InfoBlock variant="info-subtle">
                            <div className={styles['info-block']}>
                                <span className="body-small">
                                    <span className={styles.redText}>*</span>
                                    {t('common.required_field_text')}
                                </span>
                            </div>
                        </InfoBlock>
                        <div className={styles.dateSection}>
                            <div>
                                <div
                                    className={`label-medium ${styles.labelPadding}`}
                                >
                                    <p>
                                        <span className={styles.redText}>
                                            *
                                        </span>
                                        {t('register.dob_label')}
                                    </p>
                                    <Hint text={t('register.dob_help')} />
                                </div>
                                <div className={styles.selectWrapper}>
                                    <Select
                                        {...register('dayOfBirth', {
                                            required: t(
                                                'register.day_required_error'
                                            ),
                                        })}
                                        name="dayOfBirth"
                                        control={control}
										trigger={trigger}
                                        id="dayOfBirth"
                                        label=""
                                        error={
                                            errors.dayOfBirth?.message ||
                                            (typeof apiErrorMessage !==
                                                'string' &&
                                                apiErrorMessage?.DayOfBirth?.join(
                                                    '<br>'
                                                )) ||
                                            ''
                                        }
                                        errorIcon="AlertCircleFilled"
                                        required={false}
                                        placeholder={t(
                                            'register.day_placeholder'
                                        )}
                                        options={dayOptions}
                                    />
                                    <Select
                                        {...register('monthOfBirth', {
                                            required: t(
                                                'register.month_required_error'
                                            ),
                                        })}
                                        name="monthOfBirth"
                                        control={control}
										trigger={trigger}
                                        id="monthOfBirth"
                                        placeholder={t(
                                            'register.month_placeholder'
                                        )}
                                        label=""
                                        error={
                                            errors.monthOfBirth?.message ||
                                            (typeof apiErrorMessage !==
                                                'string' &&
                                                apiErrorMessage?.MonthOfBirth?.join(
                                                    '<br>'
                                                )) ||
                                            ''
                                        }
                                        errorIcon="AlertCircleFilled"
                                        required={false}
                                        options={getLocaleMonthOptions(i18n.language)}
                                    />
                                    <Select
                                        {...register('yearOfBirth', {
                                            required: t(
                                                'register.year_required_error'
                                            ),
                                        })}
                                        name="yearOfBirth"
                                        control={control}
										trigger={trigger}
                                        id="yearOfBirth"
                                        placeholder={t(
                                            'register.year_placeholder'
                                        )}
                                        label=""
                                        error={
                                            errors.yearOfBirth?.message ||
                                            (typeof apiErrorMessage !==
                                                'string' &&
                                                apiErrorMessage?.YearOfBirth?.join(
                                                    '<br>'
                                                )) ||
                                            ''
                                        }
                                        errorIcon="AlertCircleFilled"
                                        required={false}
                                        options={getYearsOptions()}
                                    />
                                </div>
                            </div>
                            <div
                                className={`body-small ${styles.contentSecondaryText}`}
                            >
                                {t('register.dob_message')}
                            </div>
                        </div>
                        {!errors?.birthDate?.message && (
                            <>
								<Insert
									{...register('email', {
										required: t(
											'register.email_required_error'
										),
										pattern: {
											value: EMAIL_PATTERN,
											message: t(
												'register.email_invalid_error'
											),
										},
									})}
                                    type='email'
									defaultValue={''}
									id="email"
									label={t('register.email_label')}
									required={true}
									error={
										errors.email?.message ||
										(typeof apiErrorMessage !==
											'string' &&
											apiErrorMessage?.Email?.join(
												'<br>'
											)) ||
										''
									}
									errorIcon="AlertCircleFilled"
									disabled={false}
								/>
                                <div className={styles.registerDataSection}>
                                    <Insert
                                        {...register('password', {
                                            validate: (value) => {
                                                if (!/^[\x00-\x7F]*$/.test(value)) {
                                                    return t('validation.password_english_characters');
                                                } else if (
                                                    !PASSWORD_PATTERN.test(
                                                        value
                                                    )
                                                ) {
                                                    return t(
                                                        'register.password_complexity_error'
                                                    );
                                                };
                                                if (
                                                    value === getValues('email')
                                                ) {
                                                    return t(
                                                        'register.password_matches_username_error'
                                                    );
                                                }
                                            },
                                        })}
                                        defaultValue={''}
                                        id="password"
                                        label={t('register.password_label')}
                                        required={true}
                                        error={
                                            errors.password?.message ||
                                            (typeof apiErrorMessage !==
                                                'string' &&
                                                apiErrorMessage?.Password?.join(
                                                    '<br>'
                                                )) ||
                                            ''
                                        }
                                        errorIcon="AlertCircleFilled"
                                        disabled={false}
                                        type="password"
                                    />
                                    <Insert
                                        {...register('passwordConfirm', {
                                            required: t(
                                                'register.password_must_match_error'
                                            ),
                                            validate: (value) =>
                                                value ===
                                                getValues('password') ||
                                                t(
                                                    'register.password_must_match_error'
                                                ),
                                        })}
                                        defaultValue={''}
                                        id="passwordConfirm"
                                        label={t(
                                            'register.confirm_password_label'
                                        )}
                                        required={true}
                                        error={
                                            errors.passwordConfirm?.message ||
                                            (typeof apiErrorMessage !==
                                                'string' &&
                                                apiErrorMessage?.PasswordConfirm?.join(
                                                    '<br>'
                                                )) ||
                                            ''
                                        }
                                        errorIcon="AlertCircleFilled"
                                        disabled={false}
                                        type="password"
                                    />
                                </div>
                                <div className={styles.offerSection}>
                                    {marketingMessage && (
                                        <div className="body-small bold">
                                            <span className={styles.redText}>
                                                *
                                            </span>
                                            {marketingMessage}
                                        </div>
                                    )}
                                    <div
                                        className={`body-small ${styles.contentSecondaryText}`}
                                    >
                                        {t('register.partner_message')}
                                    </div>
                                    <div className={styles.buttonsSection}>
                                        <Controller
                                            name="marketingOptIn"
                                            control={control}
                                            rules={{
                                                validate: (value) =>
                                                    value !== undefined ||
                                                    t(
                                                        'register.opt_in_required_error'
                                                    ),
                                            }}
                                            render={({ field }) => (
                                                <ButtonsToggler
                                                    id="marketingOptIn"
                                                    trueLabel={t(
                                                        'register.yes_label'
                                                    )}
                                                    falseLabel={t(
                                                        'register.no_label'
                                                    )}
                                                    value={field.value}
                                                    errorMessage={
                                                        errors.marketingOptIn
                                                            ?.message || ''
                                                    }
                                                    errorIcon="AlertCircleFilled"
                                                    onChange={field.onChange}
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className={styles.additionalInfo}>
                                        <div
                                            className={`body-xsmall ${styles.contentSecondaryText}`}
                                        >
                                            {t('register.opt_out_message')}
                                        </div>
                                        <div className="body-xsmall bold">
                                            {t(
                                                'register.transactional_email_message'
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {disclaimerMessage && (
                                    <div className={styles['info-block-alert']}>
                                        <InfoBlock variant="info" display="stacked">
                                            <span className="body-small">
                                                <div
                                                    className={
                                                        styles.disclaimer
                                                    }
                                                    dangerouslySetInnerHTML={{
                                                        __html: disclaimerMessage.replace(
                                                            /\[([^\]]+)\]/g,
                                                            `<a href='${urls?.privacyPolicyUrl ||
                                                            ''
                                                            }'>$1</a>`
                                                        ),
                                                    }}
                                                />
                                            </span>
                                        </InfoBlock>
                                    </div>
                                )}
                                <Button
                                    type="submit"
                                    label={t('register.register_button')}
                                    isLoading={isSubmitting}
                                    disabled={isSubmitting}
                                    variant="primary"
                                    fullWidth={true}
                                    className={styles.submitButton}
                                    dimension="large"
                                />
                                <div
                                    className={`body-small ${styles.signinText}`}
                                >
                                    <Trans
                                        i18nKey={t('common.sigin_link')}
                                        components={[
                                            <Button
                                                type="button"
                                                dimension="medium"
                                                variant="link"
                                                className={styles['signin-now']}
                                            />,
                                        ]}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </FormBox>
            </form>
        </Suspense>
    );
};

export default RegisterForm;
